import name from './name'
import { createSlice } from '@reduxjs/toolkit'
import { EncryptedCookies } from '@emerald-works-nova/components'
import Cookies from 'universal-cookie'
import jwt from 'jsonwebtoken'
import { crossSiteCookieOptions, writeCookie, expireCookie } from '../../helpers/embedded-cookies'

const cookies = new Cookies()

const CLOUD_FRONT_COOKIES = ['CloudFront-Key-Pair-Id', 'CloudFront-Policy', 'CloudFront-Signature']

const setSkillBiteLanguage = (skillBiteLanguage) => {
  if (skillBiteLanguage) {
    return localStorage.setItem('skillBiteLanguage', skillBiteLanguage)
  }

  return localStorage.removeItem('skillBiteLanguage')
}

const initialState = {
  profile: null,
  isUpdating: false,
  isProfileLoaded: false,
  hasProfileLoadError: false,
  loadingBookmarked: true,
  loadingRecommended: true,
  bookmarkedContentIds: [],
  recommendedArticleIds: [],
  authEvents: []
}

const user = createSlice({
  name,
  initialState,
  reducers: {
    setProfile: (state, { payload }) => {
      state.profile = { ...state.profile, ...payload }
      if (window.DD_RUM && typeof window.DD_RUM.setGlobalContextProperty === 'function') {
        window.DD_RUM.setGlobalContextProperty('profile', state.profile)
      } else {
        console.log('setGlobalContextProperty is not available')
      }
      state.isProfileLoaded = true
      if (payload.defaultLanguage) {
        localStorage.setItem('defaultLanguage', payload.defaultLanguage)
        if (window.DD_RUM && typeof window.DD_RUM.setGlobalContextProperty === 'function') {
          window.DD_RUM.setGlobalContextProperty('language', { language: payload.defaultLanguage })
        } else {
          console.log('setGlobalContextProperty is not available')
        }
      }

      setSkillBiteLanguage(payload.skillBiteLanguage)

      EncryptedCookies.setSession(
        JSON.stringify({
          features: state?.profile?.features,
          userGroup: state?.profile?.userGroup
        })
      )
    },
    setUpdating: (state, { payload }) => {
      state.isUpdating = payload
    },
    setProfileLoadError: (state, { payload }) => {
      state.hasProfileLoadError = payload
    },
    createCookie: (state, { payload }) => {
      const domainParts = window.location.hostname.split('.')
      const tenantKey = domainParts.shift()
      const domain = domainParts.join('.')
      const expires = new Date(new Date().getTime() + 1 * 60 * 60 * 1000)
      const allCookies = cookies.getAll()
      const cognitoCookieName = Object.keys(cookies.getAll()).find(
        cookie =>
          cookie.startsWith('CognitoIdentityServiceProvider') &&
          cookie.endsWith('idToken')
      )
      const cognitoCookie = allCookies[cognitoCookieName]

      // Edge checks only cookie existence (not payload validity) to determine auth state.
      // Writing Nova.CurrentSession without a real Cognito session causes phantom redirects.
      if (!cognitoCookie) {
        return
      }

      const cookieData = payload
      const cloudfrontCookies = payload.cloudfrontCookies
      delete cookieData.cloudfrontCookies
      if (cognitoCookie) {
        const decoded = jwt.decode(cognitoCookie)
        cookieData.exp = decoded.exp
        cookieData.auth_time = decoded.auth_time
        cookieData.iat = decoded.iat
      }
      cookieData.tenant_key = tenantKey
      cookieData.website_url = window.location.origin
      const jwtPayload = jwt.sign(
        JSON.stringify(cookieData),
        process.env.REACT_APP_JWT_SECRET || 'secret'
      )
      // Edge reads Nova.CurrentSession off the request to decide logged-in routing,
      // so inside the Learnforce iframe (PENG-3889) it has to carry the attributes
      // that let a cookie into the partitioned jar. Partitioned keeps working with
      // the parent-domain scope here: the partition key is the top-level site, which
      // is the same for every subdomain of it.
      writeCookie('Nova.CurrentSession', jwtPayload, {
        path: '/',
        domain,
        expires,
        ...crossSiteCookieOptions()
      })
      if (cloudfrontCookies) {
        Object.entries(cloudfrontCookies).forEach(([name, value]) => {
          writeCookie(name, value, {
            path: '/',
            domain: window.location.hostname,
            expires,
            secure: true,
            ...crossSiteCookieOptions()
          })
        })
        if (window.DD_RUM && typeof window.DD_RUM.setGlobalContextProperty === 'function') {
          window.DD_RUM.setGlobalContextProperty('cdn', cloudfrontCookies)
        } else {
          console.log('setGlobalContextProperty is not available')
        }
      } else {
        // Expiring a cookie only works when the write repeats the attributes it was
        // created with, so this mirrors the set above. It previously passed no
        // options at all and so never matched the path/domain-scoped cookies.
        CLOUD_FRONT_COOKIES.forEach(cookie => {
          expireCookie(cookie, {
            path: '/',
            domain: window.location.hostname,
            secure: true,
            ...crossSiteCookieOptions()
          })
        })
      }
    },
    addAuthEvent: (state, { payload }) => {
      state.authEvents.push(payload)
    },
    setAuthEvents: (state, { payload }) => {
      state.authEvents = payload
    },
    setBookmarkedContentIds: (state, { payload }) => {
      state.loadingBookmarked = false
      state.bookmarkedContentIds = payload
    },
    addBookmarked: (state, { payload }) => {
      state.bookmarkedContentIds = [
        ...state.bookmarkedContentIds,
        payload
      ]
    },
    removeBookmarked: (state, { payload }) => {
      state.bookmarkedContentIds = state.bookmarkedContentIds.filter(
        item => item.id !== payload
      )
    },
    addRecommended: (state, { payload }) => {
      state.recommendedArticleIds = [
        ...state.recommendedArticleIds,
        payload
      ]
    },
    removeRecommended: (state, { payload }) => {
      state.recommendedArticleIds = state.recommendedArticleIds.filter(
        id => id !== payload
      )
    },
    setRecommendedArticleIds: (state, { payload }) => {
      state.loadingRecommended = false
      state.recommendedArticleIds = payload
    }
  }
})

export const {
  setProfile,
  addBookmarked,
  removeBookmarked,
  setProfileLoadError,
  createCookie,
  addAuthEvent,
  setAuthEvents,
  setUpdating,
  addRecommended,
  removeRecommended,
  setBookmarkedContentIds,
  setRecommendedArticleIds
} = user.actions

export default user.reducer
